// MfaAuthSelectPanel.js
import React, { useState, useEffect } from 'react';
import styles from '../../MfaPage.module.css'; 
import axios from "../../../../utils/api/axios";
import { IAM_PATH } from '../../../../utils/Config';
import { useDialog } from '@intsoft/innocode-core';


function MfaAuthSelectPanel({ onSelectOtp, onSelectEmail, onSelectPasskey, userId }) {
    const { showDialog } = useDialog();
    const [mfaList, setMfaList] = useState([]);
    const [isLoading, setIsLoading] = useState(true); // 로딩 상태 추가

    useEffect(() => {
        if (userId) { // userId가 null, undefined, 0, '' 등 falsy 값이 아닐 때만 실행
            setIsLoading(true); // 로딩 시작
            const fetchMfaList = async () => {
                try {
                    const res = await axios.get(`${IAM_PATH}/mfa/all/${userId}`);
                    if (res.data.data) {
                        setMfaList(res.data.data);
                    } else {
                        setMfaList([]); // 데이터가 없으면 빈 배열로 명확히 설정
                    }
                } catch (err) {
                    console.error("MFA 리스트 불러오기 실패", err);
                    showDialog({
                        iconType: "warning",
                        type: 'error',
                        title: "오류",
                        message: "MFA 정보를 가져오는 데 실패했습니다.",
                        confirmText: "확인",
                    });
 
                    setMfaList([]); // 에러 발생 시에도 빈 배열로 설정
                } finally {
                    setIsLoading(false); // 로딩 종료
                }
            };
            fetchMfaList();
        } else {
            // userId가 유효하지 않은 경우, 목록을 비우고 로딩 종료
            setMfaList([]);
            setIsLoading(false);
        }
    }, [userId]);

    const isOtpActive = mfaList.some(item => item.factorType === 'TOTP' && item.status);
    const isEmailActive = mfaList.some(item => item.factorType === 'EMAIL_LINK' && item.status);
    const isPasskeyActive = mfaList.some(item => item.factorType === 'FIDO2' && item.status);

    const handleSelect = (onSelect, isActive) => {
        if (isActive) {
            onSelect();
        } else {
            showDialog({
                iconType: "info",
                type: 'info',
                title: "알림",
                message: "활성화되지 않은 인증 수단입니다.",
                confirmText: "확인",
            });
        }
    };

    return (
        <div className={styles.section_wrapper}>
            <div className={styles.section_header_wrapper}>
                <div className={styles.header_text}>2단계 인증을 진행을 위해<br className={styles.mobile_only}/> 원하시는 방법을 <br className={styles.web_only}/>선택해 <br className={styles.mobile_only}/>주세요.</div>
            </div>
            <div className={styles.section_content_wrapper}>
                <div className={styles.card_button_wrapper}>
                    <button
                        className={`${styles.card_button} ${!isOtpActive ? styles.disabled : ''}`}
                        onClick={() => handleSelect(onSelectOtp, isOtpActive)}
                        disabled={!isOtpActive}
                    >
                        <svg className="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 10.5h.01m-4.01 0h.01M8 10.5h.01M5 5h14a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1h-6.6a1 1 0 0 0-.69.275l-2.866 2.723A.5.5 0 0 1 8 18.635V17a1 1 0 0 0-1-1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z"/>
                        </svg>

                        <div className={styles.card_button_text}>OTP</div>
                    </button>
                    <button
                        className={`${styles.card_button} ${!isEmailActive ? styles.disabled : ''}`}
                        onClick={() => handleSelect(onSelectEmail, isEmailActive)}
                        disabled={!isEmailActive}
                    >
                        <svg className="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                        <path stroke="currentColor" strokeLinecap="round" strokeWidth="2" d="m3.5 5.5 7.893 6.036a1 1 0 0 0 1.214 0L20.5 5.5M4 19h16a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1Z"/>
                        </svg> 
                        <div className={styles.card_button_text}>e-mail</div>
                    </button>
                    <button
                        className={`${styles.card_button} ${!isPasskeyActive ? styles.disabled : ''}`}
                        onClick={() => handleSelect(onSelectPasskey, isPasskeyActive)}
                        disabled={!isPasskeyActive}
                    >
                        <svg className="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                        <path fill="currentColor" d="M6.94318 11h-.85227l.96023-2.90909h1.07954L9.09091 11h-.85227l-.63637-2.10795h-.02272L6.94318 11Zm-.15909-1.14773h1.60227v.59093H6.78409v-.59093ZM9.37109 11V8.09091h1.25571c.2159 0 .4048.04261.5667.12784.162.08523.2879.20502.3779.35937.0899.15436.1349.33476.1349.5412 0 .20833-.0464.38873-.1392.54119-.0918.15246-.2211.26989-.3878.35229-.1657.0824-.3593.1236-.5809.1236h-.75003v-.61367h.59093c.0928 0 .1719-.0161.2372-.0483.0663-.03314.1169-.08002.152-.14062.036-.06061.054-.13211.054-.21449 0-.08334-.018-.15436-.054-.21307-.0351-.05966-.0857-.10511-.152-.13636-.0653-.0322-.1444-.0483-.2372-.0483h-.2784V11h-.78981Zm3.41481-2.90909V11h-.7898V8.09091h.7898Z"/>
                        <path stroke="currentColor" strokeLinejoin="round" strokeWidth="2" d="M8.31818 2c-.55228 0-1 .44772-1 1v.72878c-.06079.0236-.12113.04809-.18098.07346l-.55228-.53789c-.38828-.37817-1.00715-.37817-1.39543 0L3.30923 5.09564c-.19327.18824-.30229.44659-.30229.71638 0 .26979.10902.52813.30229.71637l.52844.51468c-.01982.04526-.03911.0908-.05785.13662H3c-.55228 0-1 .44771-1 1v2.58981c0 .5523.44772 1 1 1h.77982c.01873.0458.03802.0914.05783.1366l-.52847.5147c-.19327.1883-.30228.4466-.30228.7164 0 .2698.10901.5281.30228.7164l1.88026 1.8313c.38828.3781 1.00715.3781 1.39544 0l.55228-.5379c.05987.0253.12021.0498.18102.0734v.7288c0 .5523.44772 1 1 1h2.65912c.5523 0 1-.4477 1-1v-.7288c.1316-.0511.2612-.1064.3883-.1657l.5435.2614v.4339c0 .5523.4477 1 1 1H14v.0625c0 .5523.4477 1 1 1h.0909v.0625c0 .5523.4477 1 1 1h.6844l.4952.4823c1.1648 1.1345 3.0214 1.1345 4.1863 0l.2409-.2347c.1961-.191.3053-.454.3022-.7277-.0031-.2737-.1183-.5342-.3187-.7207l-6.2162-5.7847c.0173-.0398.0342-.0798.0506-.12h.7799c.5522 0 1-.4477 1-1V8.17969c0-.55229-.4478-1-1-1h-.7799c-.0187-.04583-.038-.09139-.0578-.13666l.5284-.51464c.1933-.18824.3023-.44659.3023-.71638 0-.26979-.109-.52813-.3023-.71637l-1.8803-1.8313c-.3883-.37816-1.0071-.37816-1.3954 0l-.5523.53788c-.0598-.02536-.1201-.04985-.1809-.07344V3c0-.55228-.4477-1-1-1H8.31818Z"/>
                        </svg> 
                        <div className={styles.card_button_text}>패스키</div>
                    </button>
                </div>
            </div>
        </div>
    );
}

export default MfaAuthSelectPanel;